const PRODUCT_ART_BY_SLUG = {
    "uxd-core": "/assets/images/program-logos/uxd-core.svg",
    coretest: "/assets/images/program-logos/coretest.svg",
    healthcheck: "/assets/images/program-logos/healthcheck.svg",
    interactive: "/assets/images/program-logos/interactive.svg",
    uav: "/assets/images/program-logos/uav.svg",
    da: "/assets/images/catalog-products/da.svg",
    de: "/assets/images/catalog-products/de.svg",
    dr: "/assets/images/catalog-products/dr.svg",
    dt: "/assets/images/catalog-products/dt.svg",
    pci: "/assets/images/catalog-products/pci.svg",
    pco: "/assets/images/catalog-products/pco.svg",
    ultrapost: "/assets/images/catalog-products/ultrapost.svg",
    ledusblrg: "/assets/images/catalog-products/ledusblrg.svg",
    usbpwrtstr: "/assets/images/catalog-products/usbpwrtstr.svg",
    uxdiagnostics: "/assets/images/catalog-products/uxdiagnostics.svg",
    wstpusb: "/assets/images/catalog-products/wstpusb.svg",
    elemental: "/assets/images/catalog-products/elemental.svg",
    essential: "/assets/images/catalog-products/essential.svg",
    qtia: "/assets/images/catalog-products/qtia.svg",
    ultra: "/assets/images/catalog-products/ultra.svg",
    lpbkit: "/assets/images/catalog-products/lpbkit.png",
    ssd2usb: "/assets/images/catalog-products/ssd2usb.png",
    pstester: "/assets/images/catalog-products/pstester.png",
    qcs: "/assets/images/catalog-products/qcs.svg",
    hdc: "/assets/images/catalog-products/hdc.svg",
    qtp: "/assets/images/catalog-products/qtp.svg",
    rst5: "/assets/images/catalog-products/rst5.svg",
    rst5usb: "/assets/images/catalog-products/rst5usb.svg",
    winstress: "/assets/images/catalog-products/winstress.svg",
    winstresspro: "/assets/images/catalog-products/winstress.svg",
};

const PRODUCT_IMAGE_FIT_MODE_BY_SLUG = {
    da: "square",
    de: "square",
    dr: "square",
    hdc: "wide",
    healthcheck: "square",
    interactive: "square",
    pci: "wide",
    pco: "tall",
    qcs: "square",
    qtia: "wide",
    qtp: "wide",
    rst5: "wide",
    rst5usb: "wide",
    uav: "square",
    ultrapost: "wide",
    wstpusb: "wide",
};

const PRODUCT_BADGES_BY_SLUG = {
    pci: [
        {
            title: "Hardware Testing",
            description: "Tests core hardware subsystems for reliability and stability.",
            icon: "fas fa-microchip",
        },
        {
            title: "Behavioral Testing",
            description: "Uses behavioral diagnostics to catch intermittent faults.",
            icon: "fas fa-cogs",
        },
        {
            title: "Real-Time Readouts",
            description: "Provides live diagnostic readouts while testing is in progress.",
            icon: "fas fa-chart-line",
        },
        {
            title: "Network Ready",
            description: "Supports network-connected diagnostic workflows.",
            icon: "fas fa-network-wired",
        },
        {
            title: "Reports",
            description: "Captures structured output for review and technician workflows.",
            icon: "fas fa-file-alt",
        },
    ],
    healthcheck: [
        {
            title: "OS Checks",
            description: "Runs guided operating system validation checks.",
            icon: "fas fa-desktop",
        },
        {
            title: "Hardware Checks",
            description: "Verifies core hardware condition as part of the health scan.",
            icon: "fas fa-microchip",
        },
        {
            title: "Malware Scan",
            description: "Includes malware scanning in the health assessment workflow.",
            icon: "fas fa-shield-virus",
        },
        {
            title: "Repeatable Baselines",
            description: "Supports repeatable health assessments across multiple systems.",
            icon: "fas fa-check-circle",
        },
        {
            title: "Report Output",
            description: "Generates reports from quick or extended health scans.",
            icon: "fas fa-file-alt",
        },
    ],
    interactive: [
        {
            title: "Keyboard & Mouse",
            description: "Guided checks for input devices including keyboard and mouse.",
            icon: "fas fa-keyboard",
        },
        {
            title: "Display & GPU",
            description: "Interactive validation for monitor and graphics-related checks.",
            icon: "fas fa-desktop",
        },
        {
            title: "Touchscreen Tests",
            description: "Supports multi-point and swipe or dead-zone touchscreen checks.",
            icon: "fas fa-hand-pointer",
        },
        {
            title: "Guided Workflow",
            description: "Technician-guided flow keeps functional checks consistent.",
            icon: "fas fa-tasks",
        },
        {
            title: "Pass/Fail Reports",
            description: "Captures interactive results with pass or fail reporting.",
            icon: "fas fa-file-alt",
        },
    ],
};

const HIDDEN_PRODUCT_SLUGS = new Set([
    "ledusblrg",
    "pstester",
    "ssd2usb",
    "uxd-core",
    "usbpwrtstr",
    "uxdiagnostics",
]);

const DEFAULT_PRODUCT_IMAGE = "/assets/icons/favicon/android-chrome-512x512.png";

function normalizeSlug(value) {
    return String(value || "").trim().toLowerCase();
}

function isSvgAsset(src) {
    return String(src || "").trim().toLowerCase().endsWith(".svg");
}

function escapeHtml(value) {
    return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function isPlaceholderImage(src) {
    const normalized = String(src || "").trim().toLowerCase();
    return normalized.endsWith("/no-image.png") || normalized.includes("assets/images/product/no-image.png");
}

function normalizePresentationHtml(value) {
    return String(value || "")
        .replace(/<!--[\s\S]*?-->/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\u00a0/g, " ")
        .trim();
}

export function isWebsiteHiddenProductSlug(value) {
    return HIDDEN_PRODUCT_SLUGS.has(normalizeSlug(value));
}

function findRealProductImage(product) {
    const images = Array.isArray(product?.main_image) ? product.main_image : [];

    for (const image of images) {
        const src = String(image?.media_name || image?.thumbnail || "").trim();
        if (!src || isPlaceholderImage(src)) {
            continue;
        }

        return src;
    }

    return "";
}

export function isWebsiteCatalogDisplayableProduct(product) {
    if (isWebsiteHiddenProductSlug(product?.alt_id)) {
        return false;
    }

    const source = String(product?.catalog_source || "").trim().toLowerCase();
    const kind = String(product?.kind || "").trim().toUpperCase();

    if (source === "portal" && kind === "CUSTOM_TOOL") {
        return false;
    }

    return true;
}

export function getWebsiteProductImageUrl(product) {
    const slug = normalizeSlug(product?.alt_id);
    const fallbackArt = PRODUCT_ART_BY_SLUG[slug] || "";
    if (isSvgAsset(fallbackArt)) {
        return fallbackArt;
    }

    const primaryImage = findRealProductImage(product);
    if (primaryImage) {
        return primaryImage;
    }

    return fallbackArt || DEFAULT_PRODUCT_IMAGE;
}

export function getWebsiteProductImageFitMode(product) {
    return PRODUCT_IMAGE_FIT_MODE_BY_SLUG[normalizeSlug(product?.alt_id)] || "standard";
}

export function getWebsiteProductBadges(product) {
    const badges = Array.isArray(product?.badges) ? product.badges.filter(Boolean) : [];
    if (badges.length) {
        return badges;
    }

    return PRODUCT_BADGES_BY_SLUG[normalizeSlug(product?.alt_id)] || [];
}

export function getWebsiteProductSummaryHtml(product) {
    const details = Array.isArray(product?.details) ? product.details : [];
    const summary = normalizePresentationHtml(
        details.find((detail) => detail?.type === "SUMMARY")?.text
    );
    if (summary) {
        return summary;
    }

    const description = normalizePresentationHtml(
        details.find((detail) => detail?.type === "DESCRIPTION")?.text
    );
    if (description) {
        return description;
    }

    const fallbackDescription = String(product?.description || "").trim();
    if (fallbackDescription) {
        return `<p>${escapeHtml(fallbackDescription)}</p>`;
    }

    return "<p>Details coming soon.</p>";
}
